import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate, ROUTE_CHANGE_EVENT } from '../router';
import { trackConversion } from '../lib/analytics';
import { formatPhone, phoneTelHref, PUBLIC_PHONE } from '../content/services';
import { boxSizingStyles } from '../styles/lit-shared';
import './app-toast';
import './substack-subscribe';

const NAV_PROJECTS = [
  { path: '/blunticle', label: 'Blunticle' },
  { path: '/subbyswap', label: 'SubbySwap' },
  { path: '/traviapp', label: 'Traviapp' },
  { path: '/tag-nfc', label: 'NFC Pop' },
] as const;

const PROJECT_PATHS = NAV_PROJECTS.map((project) => project.path);

function isNavActive(pathname: string, basePath: string): boolean {
  if (basePath === '/') return pathname === '/';
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

function isProjectsActive(pathname: string): boolean {
  return PROJECT_PATHS.some((path) => isNavActive(pathname, path));
}

@customElement('app-shell')
export class AppShell extends LitElement {
  @state() private currentPath = location.pathname;
  @state() private navOpen = false;
  @state() private projectsOpen = false;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(ROUTE_CHANGE_EVENT, this._onRouteChange);
    window.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(ROUTE_CHANGE_EVENT, this._onRouteChange);
    window.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click', this._onDocumentClick);
  }

  private _onRouteChange = (e: Event) => {
    const pathname = (e as CustomEvent<{ pathname: string }>).detail?.pathname;
    this.currentPath = pathname ?? location.pathname;
    this.navOpen = false;
    this.projectsOpen = false;
  };

  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    if (this.navOpen) this.navOpen = false;
    if (this.projectsOpen) this.projectsOpen = false;
  };

  private _onDocumentClick = (e: MouseEvent) => {
    if (!this.projectsOpen) return;
    const path = e.composedPath();
    if (!path.includes(this)) {
      this.projectsOpen = false;
      return;
    }
    const projectsMenu = this.renderRoot?.querySelector('.nav-projects');
    if (projectsMenu && !path.includes(projectsMenu)) {
      this.projectsOpen = false;
    }
  };

  private closeNav() {
    this.navOpen = false;
  }

  private toggleNav() {
    this.navOpen = !this.navOpen;
  }

  private toggleProjects(e: Event) {
    e.stopPropagation();
    this.projectsOpen = !this.projectsOpen;
  }

  private get campaignLanding(): boolean {
    return this.currentPath === '/servicios';
  }

  private navLink(path: string, label: string, drawer = false) {
    const active = isNavActive(this.currentPath, path);
    const cls = drawer
      ? `nav-drawer-link ${active ? 'nav-drawer-link--active' : ''}`
      : `nav-link ${active ? 'nav-link--active' : ''}`;

    return html`
      <button
        class=${cls}
        aria-current=${active ? 'page' : 'false'}
        @click=${() => {
          this.closeNav();
          this.projectsOpen = false;
          void navigate(path);
        }}
      >
        ${label}
      </button>
    `;
  }

  private footerLink(path: string, label: string) {
    return html`
      <button class="footer-link" type="button" @click=${() => void navigate(path)}>
        ${label}
      </button>
    `;
  }

  private renderProjectsMenu() {
    const active = isProjectsActive(this.currentPath);
    return html`
      <div class="nav-projects ${this.projectsOpen ? 'nav-projects--open' : ''}">
        <button
          class="nav-link nav-projects-trigger ${active ? 'nav-link--active' : ''}"
          type="button"
          aria-expanded=${this.projectsOpen ? 'true' : 'false'}
          aria-haspopup="true"
          aria-controls="nav-projects-menu"
          @click=${this.toggleProjects}
        >
          Proyectos
          <svg
            class="nav-projects-chevron"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div
          id="nav-projects-menu"
          class="nav-projects-menu"
          role="menu"
          ?hidden=${!this.projectsOpen}
        >
          ${NAV_PROJECTS.map(
            (project) => html`
              <button
                class="nav-projects-item ${isNavActive(this.currentPath, project.path) ? 'nav-projects-item--active' : ''}"
                type="button"
                role="menuitem"
                aria-current=${isNavActive(this.currentPath, project.path) ? 'page' : 'false'}
                @click=${() => {
                  this.projectsOpen = false;
                  void navigate(project.path);
                }}
              >
                ${project.label}
              </button>
            `,
          )}
        </div>
      </div>
    `;
  }

  private renderNavDrawer() {
    return html`
      <button
        class="nav-overlay ${this.navOpen ? 'nav-overlay--visible' : ''}"
        type="button"
        aria-label="Cerrar menú"
        @click=${this.closeNav}
      ></button>
      <aside
        id="nav-drawer"
        class="nav-drawer ${this.navOpen ? 'nav-drawer--open' : ''}"
        role="dialog"
        aria-modal="true"
        aria-label="Menú de navegación"
        aria-hidden=${this.navOpen ? 'false' : 'true'}
      >
        <div class="nav-drawer-header">
          <span class="nav-drawer-title">Menú</span>
          <button
            class="nav-drawer-close"
            type="button"
            aria-label="Cerrar menú"
            @click=${this.closeNav}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <nav class="nav-drawer-nav" aria-label="Navegación principal">
          ${this.navLink('/', 'Inicio', true)}
          <div class="nav-drawer-group">
            <span class="nav-drawer-group-label">Proyectos</span>
            ${NAV_PROJECTS.map((project) => this.navLink(project.path, project.label, true))}
          </div>
          ${this.navLink('/sobre-mi', 'Sobre mí', true)}
          ${this.navLink('/agencias', 'Agencias', true)}
          ${this.navLink('/blog', 'Blog', true)}
          ${this.navLink('/contacto', 'Contacto', true)}
        </nav>
      </aside>
    `;
  }

  static styles = [
    boxSizingStyles,
    css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100dvh;
    }

    header {
      position: sticky;
      top: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-sm);
      width: 100%;
      min-width: 0;
      overflow: visible;
      height: var(--header-height);
      padding: 0 var(--space-lg);
      background: rgb(6 6 15 / 72%);
      backdrop-filter: blur(16px) saturate(160%);
      -webkit-backdrop-filter: blur(16px) saturate(160%);
      border-bottom: 1px solid var(--color-border);
    }

    .brand {
      display: inline-flex;
      align-items: center;
      text-decoration: none;
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      flex-shrink: 0;
      min-width: 0;
      transition: opacity var(--duration-fast) var(--ease-out);
    }

    .brand:hover {
      opacity: 0.9;
    }

    .brand-logo {
      display: block;
      height: 2.75rem;
      width: auto;
      flex-shrink: 0;
      /* Soft fade on all edges so the image bg blends into the header */
      -webkit-mask-image:
        linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%),
        linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
      -webkit-mask-composite: source-in;
      mask-image:
        linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%),
        linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
      mask-composite: intersect;
    }

    .menu-btn {
      display: none;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      padding: 0;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: var(--color-surface-hover);
      color: var(--color-text);
      cursor: pointer;
      flex-shrink: 0;
      transition:
        border-color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .menu-btn:hover {
      border-color: var(--color-border-glow);
    }

    .menu-btn svg {
      width: 1.25rem;
      height: 1.25rem;
    }

    main {
      flex: 1;
      width: 100%;
      /* Full-width: pages self-constrain via pageFrameStyles / full-bleed inners */
      padding-block: var(--space-xl);
    }

    footer {
      width: 100%;
      padding: var(--space-xl) var(--space-lg) var(--space-lg);
      text-align: center;
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      border-top: 1px solid var(--color-border);
      background: rgb(6 6 15 / 40%);
    }

    .footer-subscribe {
      display: block;
      max-width: 36rem;
      margin: 0 auto var(--space-xl);
      text-align: left;
    }

    .footer-brand {
      background: var(--gradient-text);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      font-weight: 600;
    }

    .footer-projects {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: var(--space-xs) var(--space-sm);
      margin-bottom: var(--space-sm);
    }

    .footer-projects-label {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      opacity: 0.85;
    }

    .footer-legal {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      align-items: center;
      gap: var(--space-xs) var(--space-sm);
      margin-bottom: var(--space-sm);
    }

    .footer-link {
      font-size: 0.8125rem;
      color: var(--color-text-subtle);
      text-decoration: none;
      cursor: pointer;
      border: none;
      background: transparent;
      padding: 0;
      transition: color var(--duration-fast) var(--ease-out);
    }

    .footer-link:hover {
      color: var(--color-text-muted);
    }

    .footer-sep {
      color: var(--color-text-subtle);
      opacity: 0.5;
      user-select: none;
    }

    .footer-tagline {
      margin: 0;
    }

    .header-call {
      margin-left: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.4rem;
      min-height: 2.75rem;
      padding: 0 0.9rem;
      border-radius: var(--radius-sm);
      border: 1px solid var(--color-border-strong);
      background: var(--color-surface);
      color: var(--color-text);
      font-weight: 600;
      font-size: 0.875rem;
      line-height: 1.2;
      text-decoration: none;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .header-call:hover {
      border-color: var(--color-border-glow);
    }

    .header-call img {
      width: 1.125rem;
      height: 1.125rem;
      flex-shrink: 0;
    }

    .header-call-short {
      display: none;
    }

    .nav-desktop {
      display: flex;
      align-items: center;
      gap: var(--space-xs);
    }

    .nav-link {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      border-radius: var(--radius-sm);
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--color-text-muted);
      text-decoration: none;
      cursor: pointer;
      border: none;
      background: transparent;
      transition:
        color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .nav-link:hover {
      color: var(--color-text);
      background: var(--color-surface-hover);
    }

    .nav-link--active {
      color: var(--color-primary-light);
      background: rgb(139 92 246 / 10%);
    }

    .nav-projects {
      position: relative;
    }

    .nav-projects-chevron {
      width: 0.875rem;
      height: 0.875rem;
      transition: transform var(--duration-fast) var(--ease-out);
    }

    .nav-projects--open .nav-projects-chevron {
      transform: rotate(180deg);
    }

    .nav-projects-menu {
      position: absolute;
      top: calc(100% + 0.35rem);
      left: 0;
      z-index: 120;
      min-width: 10.5rem;
      padding: 0.35rem;
      border-radius: var(--radius-sm);
      border: 1px solid var(--color-border);
      background: var(--palette-ink-900);
      box-shadow: var(--shadow-lg);
      display: flex;
      flex-direction: column;
      gap: 0.15rem;
    }

    .nav-projects-menu[hidden] {
      display: none;
    }

    .nav-projects-item {
      display: block;
      width: 100%;
      padding: 0.5rem 0.75rem;
      border: none;
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-text-muted);
      font-size: 0.875rem;
      font-weight: 500;
      text-align: left;
      cursor: pointer;
      transition:
        color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .nav-projects-item:hover {
      color: var(--color-text);
      background: var(--color-surface-hover);
    }

    .nav-projects-item--active {
      color: var(--color-primary-light);
      background: rgb(139 92 246 / 10%);
    }

    .cta-link {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.375rem 0.875rem;
      border-radius: var(--radius-sm);
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--palette-white);
      background: var(--gradient-primary);
      text-decoration: none;
      box-shadow: var(--shadow-glow-btn);
      transition:
        transform var(--duration-normal) var(--ease-out),
        box-shadow var(--duration-normal) var(--ease-out);
    }

    .cta-link:hover {
      transform: translateY(-1px);
      box-shadow: var(--shadow-glow-btn-hover);
      color: var(--palette-white);
      text-decoration: none;
    }

    .cta-link--drawer {
      margin-top: var(--space-xs);
      width: 100%;
      padding: var(--space-sm) var(--space-md);
      font-size: 1rem;
    }

    .nav-drawer-link {
      display: flex;
      align-items: center;
      width: 100%;
      padding: var(--space-sm) var(--space-md);
      border-radius: var(--radius-sm);
      font-size: 1rem;
      font-weight: 500;
      color: var(--color-text-muted);
      text-align: left;
      cursor: pointer;
      border: none;
      background: transparent;
      transition:
        color var(--duration-fast) var(--ease-out),
        background var(--duration-fast) var(--ease-out);
    }

    .nav-drawer-link:hover {
      color: var(--color-text);
      background: var(--color-surface-hover);
    }

    .nav-drawer-link--active {
      color: var(--color-primary-light);
      background: rgb(139 92 246 / 10%);
    }

    .nav-drawer-group {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
      padding-block: var(--space-xs);
    }

    .nav-drawer-group-label {
      padding: 0 var(--space-md);
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--color-text-subtle);
    }

    .nav-drawer-group .nav-drawer-link {
      padding-left: calc(var(--space-md) + 0.5rem);
    }

    .nav-overlay {
      display: none;
      position: fixed;
      inset: 0;
      z-index: 150;
      background: rgb(0 0 0 / 55%);
      border: none;
      padding: 0;
      cursor: pointer;
    }

    .nav-drawer {
      display: none;
      position: fixed;
      top: 0;
      left: 0;
      z-index: 200;
      width: min(280px, 85vw);
      height: 100dvh;
      padding: var(--space-lg) var(--space-md);
      background: var(--palette-ink-900);
      border-right: 1px solid var(--color-border);
      box-shadow: var(--shadow-lg);
      flex-direction: column;
      gap: var(--space-md);
      transform: translateX(-100%);
      transition: transform var(--duration-normal) var(--ease-out);
    }

    .nav-drawer--open {
      transform: translateX(0);
    }

    .nav-drawer-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-bottom: var(--space-sm);
      border-bottom: 1px solid var(--color-border);
    }

    .nav-drawer-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--color-text-muted);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .nav-drawer-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      background: transparent;
      color: var(--color-text-muted);
      cursor: pointer;
    }

    .nav-drawer-close:hover {
      color: var(--color-text);
      border-color: var(--color-border-strong);
    }

    .nav-drawer-close svg {
      width: 1.125rem;
      height: 1.125rem;
    }

    .nav-drawer-nav {
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    @media (max-width: 767px) {
      /* Colapso nav → hamburguesa */
      .menu-btn {
        display: flex;
      }

      .nav-desktop {
        display: none;
      }

      .nav-overlay {
        display: block;
      }

      .nav-overlay:not(.nav-overlay--visible) {
        pointer-events: none;
        opacity: 0;
      }

      .nav-overlay--visible {
        opacity: 1;
        transition: opacity var(--duration-normal) var(--ease-out);
      }

      .nav-drawer {
        display: flex;
      }

      /* Padding del header se reduce junto con la aparición del hamburguesa */
      header {
        padding: 0 var(--space-md);
      }

      main {
        padding-block: var(--space-lg);
      }

      footer {
        padding-inline: var(--space-md);
      }
    }

    @media (max-width: 479px) {
      /* Móviles estrechos: logo un poco más pequeño */
      .brand-logo {
        height: 1.875rem;
      }

      .header-call-full {
        display: none;
      }

      .header-call-short {
        display: inline;
      }
    }
  `,
  ];

  render() {
    const phoneLabel = PUBLIC_PHONE ? formatPhone(PUBLIC_PHONE) : '';

    return html`
      <header>
        <button class="brand" aria-label="AlejandroSN — Inicio" @click=${() => void navigate('/')}>
          <img
            class="brand-logo"
            src="/AlejandroSN_logo.png"
            alt="AlejandroSN"
            width="800"
            height="220"
          />
        </button>

        ${this.campaignLanding
          ? PUBLIC_PHONE
            ? html`
                <a
                  class="header-call"
                  href=${phoneTelHref(PUBLIC_PHONE)}
                  aria-label=${`Llamar al ${phoneLabel}`}
                  @click=${() => trackConversion('phone')}
                >
                  <img src="/whatsapp_icon.svg" alt="" width="18" height="18" />
                  <span class="header-call-full">${phoneLabel}</span>
                  <!-- <span class="header-call-short">Llamar</span> -->
                </a>
              `
            : ''
          : html`
              <nav class="nav-desktop" aria-label="Navegación principal">
                ${this.navLink('/', 'Inicio')}
                ${this.renderProjectsMenu()}
                ${this.navLink('/sobre-mi', 'Sobre mí')}
                ${this.navLink('/agencias', 'Agencias')}
                ${this.navLink('/blog', 'Blog')}
                ${this.navLink('/contacto', 'Contacto')}
              </nav>

              <button
                class="menu-btn"
                type="button"
                aria-expanded=${this.navOpen ? 'true' : 'false'}
                aria-controls="nav-drawer"
                aria-label=${this.navOpen ? 'Cerrar menú' : 'Abrir menú'}
                @click=${this.toggleNav}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            `}
      </header>

      ${this.campaignLanding ? '' : this.renderNavDrawer()}

      <main>
        <div id="outlet"></div>
      </main>
      <footer>
        ${this.campaignLanding
          ? ''
          : html`
              <substack-subscribe class="footer-subscribe" compact></substack-subscribe>
              <nav class="footer-projects" aria-label="Proyectos">
                <span class="footer-projects-label">Proyectos:</span>
                ${NAV_PROJECTS.map(
                  (project, i) => html`
                    ${i > 0 ? html`<span class="footer-sep" aria-hidden="true">·</span>` : ''}
                    ${this.footerLink(project.path, project.label)}
                  `,
                )}
              </nav>
              <nav class="footer-projects" aria-label="Colaboración">
                ${this.footerLink('/agencias', 'Agencias')}
              </nav>
            `}
        <nav class="footer-legal" aria-label="Enlaces legales">
          ${this.footerLink('/politica-privacidad', 'Política de Privacidad')}
          <span class="footer-sep" aria-hidden="true">·</span>
          ${this.footerLink('/aviso-legal', 'Aviso Legal')}
          <span class="footer-sep" aria-hidden="true">·</span>
          ${this.footerLink('/politica-cookies', 'Política de Cookies')}
        </nav>
        <p class="footer-tagline">
          <span class="footer-brand">AlejandroSN</span> &mdash; Hecho por mí, con cariño y algo de
          cabreo creativo.
        </p>
      </footer>
      <app-toast></app-toast>
      <cookie-consent></cookie-consent>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-shell': AppShell;
  }
}
